import {
  getLocationsSpecificLocationsMachines,
  selectedSpecDevices,
} from '../setting';

// Every list below is `{ target: 'switches' | 'blowers', deviceIds, body }`,
// sent by the settings page as test-device setting updates.

const withDevices = (writes) =>
  writes.filter(({ deviceIds }) => deviceIds.length > 0);

/** Body for saving the operator's unit: toggle 0 is °F, anything else °C. */
export const unitsPayload = (toggle) => ({
  user: { temperature_unit: toggle === 0 ? 'f' : 'c' },
});

/** The four wind-factor triggers (low, med, high, extreme) for every device. */
export const windThresholdWrites = ({ temps, deviceIds }) => {
  const body = { wind_threshold: temps.map(Number) };
  return withDevices([
    { target: 'switches', deviceIds: deviceIds.ess, body },
    { target: 'blowers', deviceIds: deviceIds.blowers, body },
  ]);
};

/**
 * Snow-sensor triggers. `temps` holds each system's new trigger, or null
 * when its field is empty. ESS switches are written only when their trigger
 * changed; TGS and TES share the blowers, so both go in one write when both
 * are set.
 */
export const snowThresholdWrites = ({ temps, essChanged, deviceIds }) => {
  const writes = [];
  if (essChanged) {
    writes.push({
      target: 'switches',
      deviceIds: deviceIds.ess,
      body: { snow_threshold: temps.ess },
    });
  }
  if (temps.tgs != null && temps.tes != null) {
    writes.push({
      target: 'blowers',
      deviceIds: deviceIds.blowers,
      body: {
        blower_snow_threshold: temps.tgs,
        electrical_snow_threshold: temps.tes,
      },
    });
  } else if (temps.tgs != null) {
    writes.push({
      target: 'blowers',
      deviceIds: deviceIds.tgs,
      body: { blower_snow_threshold: temps.tgs },
    });
  } else if (temps.tes != null) {
    writes.push({
      target: 'blowers',
      deviceIds: deviceIds.tes,
      body: { electrical_snow_threshold: temps.tes },
    });
  }
  return withDevices(writes);
};

/** Body for an ATS option. */
export const atsPayload = (option) => ({ EBP: option });

// The system-commands t/c channels, in the order of `isSave` and `tCNumber`.
// `target: 'byType'` follows each device's `deviceType`; `id` says whether the
// device is addressed by its key or by its `deviceMac`.
const TC_CHANNELS = [
  {
    key: 'outsideTemp',
    flag: 'isOutsideTempSelected',
    tcSys: 'outsideTempTCNumber',
    field: 'outside_temp_ch',
    target: 'byType',
    id: 'machine',
  },
  {
    key: 'burningChamber',
    flag: 'isBurningChamberSelected',
    tcSys: 'burningChamberTCNumber',
    field: 'burner_temp_ch',
    target: 'blowers',
    id: 'deviceMac',
  },
  {
    key: 'encloseTemp',
    flag: 'isEncloseTempSelected',
    tcSys: 'encloseTempTCNumber',
    field: 'enclosure_temp_ch',
    target: 'byType',
    id: 'machine',
  },
  {
    key: 'currEss',
    flag: 'isCurrEssSelected',
    tcSys: 'currEssTCNumber',
    field: 'display_temp_ch',
    target: 'switches',
    id: 'deviceMac',
  },
  {
    key: 'currTgs',
    flag: 'isCurrTgsSelected',
    tcSys: 'currTgsTCNumber',
    field: 'blower_temp_ch',
    target: 'blowers',
    id: 'deviceMac',
  },
  {
    key: 'currTes',
    flag: 'isCurrTesSelected',
    tcSys: 'currTesTCNumber',
    field: 'display_temp_ch',
    target: 'blowers',
    id: 'deviceMac',
  },
];

const TARGET_OF_DEVICE_TYPE = { switches: 'switches', blowers: 'blowers' };

/** The label the settings slice stores for a zero-based thermocouple choice. */
export const thermocoupleLabel = (tc) =>
  `tc${tc < 9 ? `0${tc + 1}` : tc + 1}`;

/**
 * The system-commands t/c channels the operator ticked (`isSave`), each set
 * to its chosen thermocouple (`tCNumber`, zero-based; the outside temperature
 * can also be 'internet'). Returns the writes, the slice updates
 * (`handleSelectTC` payloads), how many channels were ticked and how many
 * devices will be written.
 */
export const thermocoupleChannelWrites = ({ sysSpec, isSave, tCNumber }) => {
  const writes = [];
  const selections = [];
  let channels = 0;
  let devicesCount = 0;

  TC_CHANNELS.forEach((channel, idx) => {
    if (!isSave[channel.key]) {
      return;
    }
    channels += 1;
    const tc = tCNumber[idx];
    // Only the outside-temperature choice can be 'internet' (the page passes
    // it through); the other channels' selects only offer numbers.
    const body = { [channel.field]: tc === 'internet' ? tc : tc + 1 };
    const ids = { switches: [], blowers: [] };

    selectedSpecDevices(sysSpec, channel.flag).forEach(
      ({ location, specificLocation, machine, device }) => {
        const target =
          channel.target === 'byType'
            ? TARGET_OF_DEVICE_TYPE[device.deviceType]
            : channel.target;
        if (target) {
          ids[target].push(channel.id === 'machine' ? machine : device.deviceMac);
          devicesCount += 1;
        }
        selections.push({
          location,
          specificLocation,
          machine,
          selectedTCSys: channel.tcSys,
          tcNum: thermocoupleLabel(tc),
        });
      },
    );

    ['switches', 'blowers'].forEach((target) => {
      if (ids[target].length > 0) {
        writes.push({ target, deviceIds: ids[target], body });
      }
    });
  });

  return { writes, selections, channels, devicesCount };
};

/** Track temperature control: mode 0 turns it on, 1 off. */
export const trackTempControlPayload = (activate) => ({
  tc_mode: activate ? 0 : 1,
});

/** Gas type: choice 0 is LP, anything else NG. */
export const gasTypePayload = (gasSelection) => ({
  Gas_type: gasSelection === 0 ? 'LP' : 'NG',
});

/**
 * The TGS devices whose valve positions the operator confirmed. Positions
 * live in `admin.tgs`, keyed by the device's sub-location when it has one,
 * otherwise by its zone.
 */
export const confirmedValves = (tgsSpec, tgs) =>
  getLocationsSpecificLocationsMachines(Object.entries(tgsSpec ?? {}))
    .map(([location, machine, specificLocation]) => {
      const valveLocation = specificLocation || location;
      return {
        machine,
        valveLocation,
        gasValue: tgs?.[valveLocation]?.[machine]?.gasValue,
      };
    })
    .filter(({ gasValue }) => gasValue?.isConfirm);

/** Body for the confirmed valve positions. */
export const valveSettingsPayload = (valves) => ({
  valve_settings: valves.map(({ machine, gasValue }) => ({
    deviceId: machine,
    initial_open: gasValue.startPosition,
    min_open: gasValue.minPosition,
    max_open: gasValue.maxPosition,
  })),
  testDevices: true,
});

/** Force gas & electric on together for 15 minutes (1) or not (0). */
export const forceGasAndElectricPayload = (enable) => ({
  force: enable ? 1 : 0,
});
